import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";

const copyTextToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy method
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

export const HereWeGoModal = ({ open, player, onClose }) => {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) setCopied(false);
  }, [open]);

  if (!player) return null;

  const breakingText = t("exclusiveText", {
    name: player.name,
    city: player.city,
    style: player.style,
    position: player.position,
  });

  const handleCopy = async () => {
    const ok = await copyTextToClipboard(breakingText);
    if (ok) {
      setCopied(true);
      toast.success(t("toastCopied"));
      setTimeout(() => setCopied(false), 2500);
    }
  };

  // Render the translated template with highlighted player values
  const renderExclusive = () => {
    const template = t("exclusiveText");
    const parts = template.split(/(\{name\}|\{city\}|\{style\}|\{position\})/g);
    return parts.map((part, i) => {
      if (part === "{name}")
        return (
          <span key={i} className="font-bold text-[hsl(var(--navy))]">
            {player.name}
          </span>
        );
      if (part === "{city}")
        return (
          <span key={i} className="font-bold text-[hsl(var(--navy))]">
            {player.city}
          </span>
        );
      if (part === "{style}")
        return (
          <span key={i} className="font-semibold text-[hsl(var(--news-red))]">
            {player.style}
          </span>
        );
      if (part === "{position}")
        return (
          <span key={i} className="font-semibold text-[hsl(var(--news-red))]">
            {player.position}
          </span>
        );
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="here-we-go-modal"
        className="max-w-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] p-0 overflow-hidden gap-0"
      >
        {/* Red breaking strip */}
        <div className="h-[6px] w-full bg-[hsl(var(--news-red))]" />

        <div className="px-6 sm:px-8 pt-6 pb-8 text-center">
          <motion.div
            initial={{ scale: 1 }}
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.15 }}
            className="text-5xl mb-3"
            aria-hidden="true"
          >
            🚨
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <DialogTitle
              data-testid="here-we-go-headline"
              className="font-display uppercase text-5xl sm:text-6xl leading-none text-[hsl(var(--news-red))] tracking-[0.06em]"
            >
              {t("hereWeGo")}
            </DialogTitle>
          </motion.div>

          <div className="mt-2 mb-5">
            <span className="inline-block font-display uppercase tracking-[0.2em] text-xs bg-[hsl(var(--navy))] text-white px-3 py-1 rounded">
              {t("officialConfirmed")}
            </span>
          </div>

          <DialogDescription
            data-testid="here-we-go-text"
            className="text-base sm:text-lg leading-relaxed text-[hsl(var(--ink))]"
          >
            {renderExclusive()}
          </DialogDescription>

          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              data-testid="share-to-chat-button"
              onClick={handleCopy}
              className={
                copied
                  ? "bg-[hsl(142,72%,35%)] text-white hover:bg-[hsl(142,72%,30%)] px-6 h-11 font-semibold w-full sm:w-auto transition-colors"
                  : "bg-[hsl(var(--news-red))] text-white hover:bg-[hsl(var(--news-red-dark))] px-6 h-11 font-semibold w-full sm:w-auto active:scale-[0.98] transition-colors"
              }
            >
              {copied ? (
                <Check className="h-4 w-4 me-2" />
              ) : (
                <Share2 className="h-4 w-4 me-2" />
              )}
              {copied ? t("copied") : t("copyToChat")}
            </Button>
            <Button
              data-testid="here-we-go-modal-close-button"
              onClick={onClose}
              variant="outline"
              className="border-[hsl(var(--navy))] text-[hsl(var(--navy))] hover:bg-[hsl(var(--navy))] hover:text-white active:scale-[0.98] transition-colors px-8 h-11 font-semibold w-full sm:w-auto"
            >
              {t("close")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
